
import { Component, Provide, Prop, Mixins } from "vue-property-decorator";
import { CreateElement } from "vue";
import { convertObjAb2Json, downloadBlob } from "@/helpers/file";
import _ from "@/boot/lodash";

// Mixins
import RequestPromises from "@/mixins/requestPromises";
import { FilteringSortingTableStore } from "@/mixins/tableWithFilteringAndSortingStore";

// Enums/Data
import { DataModules } from "@/store/modules/data";
import { Sorters } from "@/data/table";
import { DefaultReportsFilters } from "@/data/filters/reports";
import { DefaultReportsSorters } from "@/data/sorters/reports";

// Types
import {
  IGeneratedReportsProviderData,
  IGeneratedReportsProviderMethods
} from "@/models/providers/reports";

import { IReport } from "@/models/reports";
import { ITableConfig, IFilter, ISorter } from "@/models/tables";
import { AxiosError } from "axios";

@Component
export default class GeneratedReportsProvider extends Mixins(
  RequestPromises,
  FilteringSortingTableStore
) {
  // Props

  @Prop({ required: true })
  public code!: IReport["report_code"];

  @Prop({ required: true })
  public helpParams!: any;

  @Prop({ default: () => DefaultReportsFilters })
  public availableFilters!: (vm: GeneratedReportsProvider) => IFilter[];

  @Prop({ default: () => DefaultReportsSorters })
  public availableSorters!: (vm: GeneratedReportsProvider) => ISorter[];

  @Prop({ default: () => ({}) })
  public readonly manualFilters!: Record<string, any>;

  @Prop({ default: false })
  public handleLoading!: boolean;

  @Prop({ default: () => ({}) })
  public loaderAttrs!: Record<string, any>;

  @Prop({ default: true })
  public handleRequestError!: boolean;

  @Prop({ default: true })
  public handleGuarding!: boolean;

  // Variables

  public dataModule: DataModules = DataModules.REPORTS;

  public defaults: ITableConfig = {
    page: 1,
    limit: this.limit,
    sort: {
      field: "created_at",
      direction: Sorters.DESCENDING
    },
    filters: []
  };

  public filters = this.reportsFilters;
  public sorters = this.reportsSorters;

  // Computed

  public get reportsFilters() {
    return this.availableFilters(this);
  }

  public get reportsSorters() {
    return this.availableSorters(this);
  }

  // Lifecycle methods

  public async created() {
    if (!this.contentIsAvailable) return;

    this.apiProps = {
      ...this.manualFilters
    };

    this.getData();
    this.$bus.$on("refreshGeneratedReportsList", this.reloadData);
  }

  public beforeDestroy() {
    this.$bus.$off("refreshGeneratedReportsList");
  }

  // Methods

  public reloadData() {
    return this.getData({ ignoreStored: true });
  }

  public getParamsValues(params) {
    const paramsObj = _.pickBy(
      params,
      (value, key) => !["limit", "offset", "report"].includes(key)
    );

    if (_.isEmpty(paramsObj)) {
      return [this.$t("_.no_filters_applied")];
    }

    return _.map(paramsObj, (paramValue, paramKey) => {
      const helpParam = this.$_.find(this.helpParams, ["code", paramKey]);
      let results = "";

      if (helpParam.options.length) {
        results = this.$_.map(paramValue, resultValue => {
          return this.$_.get(
            this.$_.find(helpParam.options, ["value", resultValue]),
            "label",
            resultValue
          );
        }).join(", ");
      } else {
        results = paramValue;
      }

      return `${helpParam.name}: ${results}`;
    });
  }

  // Methods

  public async deleteReport(reportId) {
    await this.$store.dispatch(`data/${DataModules.REPORTS}/remove`, {
      reportId
    });
    this.reloadData();
  }

  public async downloadReport(report) {
    if (!this.canDownload) return;
    try {
      const data = await this.$store.dispatch(
        `data/${DataModules.REPORTS}/download`,
        {
          reportCode: this.code,
          params: {
            report_id: report.id
          },
          returnData: true,
          vm: this
        }
      );

      const blob = new Blob([data], {
        type: "text/csv;charset=utf-8;"
      });

      downloadBlob(blob, report.filename);
    } catch (e) {
      const error = e as AxiosError | any;
      convertObjAb2Json(error, "response.data");
      // Check for 404 (not found)
      if (this.$_.get(error, "response.status") === 404) {
        return this.$store.dispatch("toast/warning", {
          message: this.$t(
            "_sentence.pdf_is_being_generated_and_will_be_available_shortly"
          )
        });
      }
      this.$store.dispatch("api/handleError", error);
    }
  }

  // Computed

  public get canDownload() {
    return this.$userCan("reports_download");
  }

  public get canDelete() {
    return this.$userCan("reports_destroy_prepared");
  }

  // Guarding

  public guardFunctionalities = ["reports_index_prepared"];

  public get contentIsAvailable() {
    return this.$userCan(...this.guardFunctionalities);
  }

  // Provide

  @Provide("generatedReportsProviderData")
  public providerData(): IGeneratedReportsProviderData {
    return {
      activeFilters: this.activeFilters,
      activeSort: this.table.sort,
      availableFilters: this.filters,
      availableSorters: this.sorters,
      canDelete: this.canDelete,
      canDownload: this.canDownload,
      enableFiltering: this.enableFiltering,
      enableSorting: this.enableSorting,
      filters: this.filters,
      isLoading: this.table.loading || this.loading,
      limit: this.limit,
      page: this.table.page,
      reports:
        this.loading || this.table.loading || !this.total
          ? Array(this.total || 5)
          : (this.table.data as any),
      sorters: this.sorters,
      table: this.table,
      total: this.total
    };
  }

  @Provide("generatedReportsProviderMethods")
  public get providerMethods(): IGeneratedReportsProviderMethods {
    return {
      deleteReport: this.deleteReport,
      downloadReport: this.downloadReport,
      getData: this.getData,
      getParamsValues: this.getParamsValues,
      onFiltersChange: this.onFiltersChange,
      onLimitChange: this.onLimitChange,
      onPageChange: this.onPageChange,
      onSort: this.onSort,
      reloadData: this.reloadData
    };
  }

  // Render

  public render(h: CreateElement) {
    // Handle guarding
    if (this.handleGuarding && !this.contentIsAvailable)
      return h("guard", { props: { ifAdmin: false } });

    // Handle loading
    if (this.handleLoading && this.loading)
      return h("is-loading", { props: { if: true } });

    // Handle request error
    if (this.handleRequestError && this.requestError)
      return h("request-error", {
        props: { isLoading: this.table.loading },
        on: { click: () => this.reloadData() }
      });

    return (
      this.$scopedSlots?.default &&
      this.$scopedSlots.default({
        $reportsData: this.providerData(),
        $reportsMethods: this.providerMethods
      })
    );
  }
}
